

// ═══════════════════════════════════════════════════════════════════════════
// RUN 24: Cross-Reference Validation
// ═══════════════════════════════════════════════════════════════════════════

import {
  safeAlert, reportResult, rangeDetail,
  RunDetail, SRS_NAV_PREFIX, MAX_NAV_ITEMS
} from '../runs/common';
import { getConfig } from '../taskpane/configService';

/** Bookmark prefix for this run */
const BM_PREFIX = SRS_NAV_PREFIX + "R24_";

// ─────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────

const W_NS   = "http://schemas.openxmlformats.org/wordprocessingml/2006/main";

// XREF_PATTERNS is built from config inside run24() and passed to helpers.

// isTocOrCaptionStyle is built from config inside run24() and passed to helpers.
let _tocCaptionStyles: string[] = [];

function isTocOrCaptionStyle(style: string): boolean {
  const s = (style || "").replace(/\s/g, "").toLowerCase();
  return _tocCaptionStyles.some(token =>
    token === "heading" ? s.startsWith("heading") :
    token.startsWith("toc")   ? /^toc\d*$/.test(s) || s.includes(token) :
    s.includes(token)
  );
}

// Check if a paragraph is a caption definition (not a reference)
// e.g., "Figure 2.1 Sample Figure Title" or "Table 6.1 Demographic Summary"
// These paragraphs START with the pattern and are the caption itself.
function isCaptionParagraph(text: string): boolean {
  const t = text.trim();
  return /^(?:Table|Figure)\s+\d+[\.\-]\d+(?:[\.\-]\d+)*(?:\.[a-z])?\s+\S/i.test(t);
}

// ─────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────

interface FieldRefInfo {
  instrText: string;      // e.g., " REF _Ref67890 \\h "
  bookmark: string;       // e.g., "_Ref67890"
  resultText: string;     // visible text, e.g., "Table 2.1"
  paraIndex: number;
  type: "REF" | "PAGEREF" | "NOTEREF";
}

interface HyperlinkInfo {
  anchor: string;
  text: string;
  paraIndex: number;
  paraText: string;
  type: "toc" | "internal" | "external";
}

interface BookmarkInfo {
  name: string;
  paraIndex: number;
}

interface UnlinkedRef {
  pattern: string;       // e.g., "Table 2.1"
  label: string;         // e.g., "Table"
  paraIndex: number;
  paraText: string;
}

// ─────────────────────────────────────────────────────────────────────────
// XML Helpers
// ─────────────────────────────────────────────────────────────────────────

function toArray<T extends Element>(
  collection: HTMLCollectionOf<Element> | NodeListOf<Element>
): T[] {
  const result: T[] = [];
  for (let i = 0; i < collection.length; i++) result.push(collection.item(i) as T);
  return result;
}

function getTextContent(el: Element): string {
  const tTags = el.getElementsByTagNameNS(W_NS, "t");
  let txt = "";
  for (let i = 0; i < tTags.length; i++) txt += tTags.item(i).textContent || "";
  return txt;
}

// ─────────────────────────────────────────────────────────────────────────
// Phase 1a: Extract field codes (REF, PAGEREF, NOTEREF)
// ─────────────────────────────────────────────────────────────────────────

function extractFieldRefs(xmlDoc: Document): FieldRefInfo[] {
  const fieldRefs: FieldRefInfo[] = [];
  const paras = toArray(xmlDoc.getElementsByTagNameNS(W_NS, "p"));

  for (let i = 0; i < paras.length; i++) {
    const para = paras[i];

    // ── Simple fields: <w:fldSimple w:instr="..."> ──
    const fldSimples = para.getElementsByTagNameNS(W_NS, "fldSimple");
    for (let f = 0; f < fldSimples.length; f++) {
      const fld = fldSimples.item(f);
      const instr = (fld.getAttribute("w:instr") || "").trim();
      const refMatch = instr.match(/^\s*(REF|PAGEREF|NOTEREF)\s+(\S+)/i);
      if (refMatch) {
        const resultText = getTextContent(fld as Element).trim();
        fieldRefs.push({
          instrText: instr,
          bookmark: refMatch[2],
          resultText,
          paraIndex: i,
          type: refMatch[1].toUpperCase() as "REF" | "PAGEREF" | "NOTEREF",
        });
      }
    }

    // ── Complex fields: fldChar begin → instrText → fldChar separate → result → fldChar end ──
    const runs = toArray(para.getElementsByTagNameNS(W_NS, "r"));
    let inField = false;
    let instrAccum = "";
    let resultAccum = "";
    let pastSeparator = false;

    for (let r = 0; r < runs.length; r++) {
      const run = runs[r];

      // Check for fldChar elements
      const fldChars = run.getElementsByTagNameNS(W_NS, "fldChar");
      if (fldChars.length > 0) {
        const fldType = fldChars.item(0).getAttribute("w:fldCharType") || "";

        if (fldType === "begin") {
          inField = true;
          instrAccum = "";
          resultAccum = "";
          pastSeparator = false;
        } else if (fldType === "separate") {
          pastSeparator = true;
        } else if (fldType === "end") {
          if (inField) {
            const refMatch = instrAccum.match(/^\s*(REF|PAGEREF|NOTEREF)\s+(\S+)/i);
            if (refMatch) {
              fieldRefs.push({
                instrText: instrAccum.trim(),
                bookmark: refMatch[2],
                resultText: resultAccum.trim(),
                paraIndex: i,
                type: refMatch[1].toUpperCase() as "REF" | "PAGEREF" | "NOTEREF",
              });
            }
          }
          inField = false;
          instrAccum = "";
          resultAccum = "";
          pastSeparator = false;
        }
        continue;
      }

      if (!inField) continue;

      if (!pastSeparator) {
        const instrEls = run.getElementsByTagNameNS(W_NS, "instrText");
        for (let t = 0; t < instrEls.length; t++) {
          instrAccum += instrEls.item(t).textContent || "";
        }
      } else {
        const tEls = run.getElementsByTagNameNS(W_NS, "t");
        for (let t = 0; t < tEls.length; t++) {
          resultAccum += tEls.item(t).textContent || "";
        }
      }
    }
  }

  return fieldRefs;
}

// ─────────────────────────────────────────────────────────────────────────
// Phase 1b: Extract w:hyperlink elements (TOC entries + explicit links)
// ─────────────────────────────────────────────────────────────────────────

function extractHyperlinks(xmlDoc: Document): HyperlinkInfo[] {
  const hyperlinks: HyperlinkInfo[] = [];
  const paras = toArray(xmlDoc.getElementsByTagNameNS(W_NS, "p"));

  for (let i = 0; i < paras.length; i++) {
    const para = paras[i];
    const paraText = getTextContent(para).trim();

    // Get paragraph style to classify TOC vs non-TOC
    let style = "";
    const pPr = para.getElementsByTagNameNS(W_NS, "pPr");
    if (pPr.length > 0) {
      const pStyle = pPr.item(0).getElementsByTagNameNS(W_NS, "pStyle");
      if (pStyle.length > 0) {
        style = pStyle.item(0).getAttribute("w:val") || "";
      }
    }

    const hypElements = para.getElementsByTagNameNS(W_NS, "hyperlink");

    for (let h = 0; h < hypElements.length; h++) {
      const hyp = hypElements.item(h);
      const anchor = hyp.getAttribute("w:anchor") || "";
      const rId = hyp.getAttribute("r:id") || "";
      const hypText = getTextContent(hyp as Element).trim();

      let linkType: "toc" | "internal" | "external" = "internal";
      if (anchor && anchor.startsWith("_Toc") && isTocOrCaptionStyle(style)) {
        linkType = "toc";
      } else if (rId && !anchor) {
        linkType = "external";
      }

      if (anchor || rId) {
        hyperlinks.push({
          anchor: anchor || rId,
          text: hypText,
          paraIndex: i,
          paraText: paraText.substring(0, 100),
          type: linkType,
        });
      }
    }
  }

  return hyperlinks;
}

// ─────────────────────────────────────────────────────────────────────────
// Phase 1c: Extract bookmarks
// ─────────────────────────────────────────────────────────────────────────

function extractBookmarks(xmlDoc: Document): BookmarkInfo[] {
  const bookmarks: BookmarkInfo[] = [];
  const paras = toArray(xmlDoc.getElementsByTagNameNS(W_NS, "p"));

  for (let i = 0; i < paras.length; i++) {
    const para = paras[i];
    const bmStarts = para.getElementsByTagNameNS(W_NS, "bookmarkStart");

    for (let b = 0; b < bmStarts.length; b++) {
      const name = bmStarts.item(b).getAttribute("w:name") || "";
      if (name && name !== "_GoBack") {
        bookmarks.push({ name, paraIndex: i });
      }
    }
  }

  return bookmarks;
}

// ─────────────────────────────────────────────────────────────────────────
// Phase 1d: Extract paragraph data with field-aware linked text detection
// ─────────────────────────────────────────────────────────────────────────

interface ParaInfo {
  index: number;
  text: string;
  style: string;
  linkedTexts: string[];     // text inside field codes OR non-TOC hyperlinks
  isCaption: boolean;        // paragraph starts with "Table X.X ..." or "Figure X.X ..."
}

function extractParaData(
  xmlDoc: Document,
  fieldRefs: FieldRefInfo[],
  hyperlinks: HyperlinkInfo[]
): ParaInfo[] {
  const data: ParaInfo[] = [];
  const paras = toArray(xmlDoc.getElementsByTagNameNS(W_NS, "p"));

  // Build per-paragraph maps of linked texts from fields and hyperlinks
  const fieldTextsByPara = new Map<number, string[]>();
  for (const f of fieldRefs) {
    if (!fieldTextsByPara.has(f.paraIndex)) fieldTextsByPara.set(f.paraIndex, []);
    if (f.resultText) fieldTextsByPara.get(f.paraIndex).push(f.resultText);
  }

  const hypTextsByPara = new Map<number, string[]>();
  for (const h of hyperlinks) {
    if (h.type === "toc") continue;
    if (!hypTextsByPara.has(h.paraIndex)) hypTextsByPara.set(h.paraIndex, []);
    if (h.text) hypTextsByPara.get(h.paraIndex).push(h.text);
  }

  for (let i = 0; i < paras.length; i++) {
    const para = paras[i];
    const text = getTextContent(para).trim();

    let style = "";
    const pPr = para.getElementsByTagNameNS(W_NS, "pPr");
    if (pPr.length > 0) {
      const pStyle = pPr.item(0).getElementsByTagNameNS(W_NS, "pStyle");
      if (pStyle.length > 0) {
        style = pStyle.item(0).getAttribute("w:val") || "";
      }
    }

    const linkedTexts: string[] = [
      ...(fieldTextsByPara.get(i) || []),
      ...(hypTextsByPara.get(i) || []),
    ];

    data.push({
      index: i,
      text,
      style,
      linkedTexts,
      isCaption: isCaptionParagraph(text),
    });
  }

  return data;
}

// ─────────────────────────────────────────────────────────────────────────
// Phase 2: Find unlinked cross-reference patterns
// ─────────────────────────────────────────────────────────────────────────

function findUnlinkedReferences(paraData: ParaInfo[], xrefPatterns: { label: string; regex: RegExp }[]): UnlinkedRef[] {
  const unlinked: UnlinkedRef[] = [];
  // Track pattern+paraIndex to avoid duplicates when same text appears
  // multiple times in the same paragraph (e.g., "See Table 2.1 and Table 2.1")
  const seenInPara = new Set<string>();

  for (const p of paraData) {
    // Skip TOC entries, captions, headings
    if (isTocOrCaptionStyle(p.style)) continue;

    // Skip empty paragraphs
    if (!p.text || p.text.length === 0) continue;

    // Skip caption paragraphs ("Figure 2.1 Sample Title" — the definition)
    if (p.isCaption) continue;

    for (const pattern of xrefPatterns) {
      pattern.regex.lastIndex = 0;
      let match: RegExpExecArray | null;

      while ((match = pattern.regex.exec(p.text)) !== null) {
        const refText = match[0];
        const matchPos = match.index;

        // ── Dedup: same pattern in same paragraph → only one entry ──
        const dedupKey = `${p.index}|${refText}`;
        if (seenInPara.has(dedupKey)) continue;

        // Check if this text appears inside any linked text (field code or hyperlink)
        const isLinked = p.linkedTexts.some((lt) => {
          const ltLower = lt.toLowerCase();
          const refLower = refText.toLowerCase();
          return ltLower.includes(refLower) || refLower.includes(ltLower);
        });

        if (isLinked) continue;

        // Guard: match at position 0 that looks like a caption
        if (matchPos === 0 && /^(?:Table|Figure)\s+\d/i.test(p.text)) continue;

        // Guard: "Protocol Section X.X" — external protocol reference, not internal
        const preceding20 = p.text.substring(Math.max(0, matchPos - 20), matchPos);
        if (/Protocol\s*$/i.test(preceding20)) continue;

        seenInPara.add(dedupKey);
        unlinked.push({
          pattern: refText,
          label: pattern.label,
          paraIndex: p.index,
          paraText: p.text.substring(0, 100),
        });
      }
    }
  }

  return unlinked;
}

// ─────────────────────────────────────────────────────────────────────────
// Phase 3: Detect broken field references and hyperlinks
// ─────────────────────────────────────────────────────────────────────────

interface BrokenRef {
  text: string;
  bookmark: string;
  source: "field" | "hyperlink";
  paraIndex: number;
}

function findBrokenRefs(
  fieldRefs: FieldRefInfo[],
  hyperlinks: HyperlinkInfo[],
  bookmarks: BookmarkInfo[]
): BrokenRef[] {
  const bookmarkNames = new Set(bookmarks.map((b) => b.name));
  const broken: BrokenRef[] = [];

  for (const f of fieldRefs) {
    if (!bookmarkNames.has(f.bookmark)) {
      broken.push({
        text: f.resultText || f.instrText,
        bookmark: f.bookmark,
        source: "field",
        paraIndex: f.paraIndex,
      });
    }
  }

  for (const h of hyperlinks) {
    if (h.type === "toc") continue;
    if (h.type !== "internal") continue;
    if (!bookmarkNames.has(h.anchor)) {
      broken.push({
        text: h.text,
        bookmark: h.anchor,
        source: "hyperlink",
        paraIndex: h.paraIndex,
      });
    }
  }

  return broken;
}

// ─────────────────────────────────────────────────────────────────────────
// Phase 4: Categorize counts
// ─────────────────────────────────────────────────────────────────────────

interface RefCounts {
  tableRefs:    { linked: number; unlinked: number };
  figureRefs:   { linked: number; unlinked: number };
  sectionRefs:  { linked: number; unlinked: number };
  appendixRefs: { linked: number; unlinked: number };
}

function categorizeRefs(
  fieldRefs: FieldRefInfo[],
  unlinked: UnlinkedRef[]
): RefCounts {
  const counts: RefCounts = {
    tableRefs:    { linked: 0, unlinked: 0 },
    figureRefs:   { linked: 0, unlinked: 0 },
    sectionRefs:  { linked: 0, unlinked: 0 },
    appendixRefs: { linked: 0, unlinked: 0 },
  };

  for (const f of fieldRefs) {
    const t = f.resultText.toLowerCase();
    if (/^table\s/i.test(t))         counts.tableRefs.linked++;
    else if (/^figure\s/i.test(t))   counts.figureRefs.linked++;
    else if (/^section\s/i.test(t))  counts.sectionRefs.linked++;
    else if (/^appendix\s/i.test(t)) counts.appendixRefs.linked++;
  }

  for (const ref of unlinked) {
    switch (ref.label) {
      case "Table":    counts.tableRefs.unlinked++;    break;
      case "Figure":   counts.figureRefs.unlinked++;   break;
      case "Section":  counts.sectionRefs.unlinked++;  break;
      case "Appendix": counts.appendixRefs.unlinked++; break;
    }
  }

  return counts;
}

// ─────────────────────────────────────────────────────────────────────────
// Phase 5: Insert comments at issues using Office.js
// ─────────────────────────────────────────────────────────────────────────

async function insertCommentsAtIssues(
  context: Word.RequestContext,
  unlinked: UnlinkedRef[],
  broken: BrokenRef[],
  navDetails: RunDetail[],
  navCounter: { count: number }
): Promise<number> {
  let commentsInserted = 0;
  const MAX_COMMENTS = 50;
  const body = context.document.body;

  // ── Master dedup: tracks every paragraph+pattern combo that has been commented ──
  // Key format: "paraTextFirst60|pattern" — prevents commenting the same
  // reference in the same paragraph twice regardless of which UnlinkedRef
  // entry triggered it.
  const commentedLocations = new Set<string>();

  // ── Step 0: Deduplicate unlinked refs ──
  // Same pattern in the same paragraph should only produce one comment.
  // Also collapse overlapping patterns (e.g. "Section 3.1" vs "Section 3.1.2"
  // in the same paragraph — keep the longer one).
  const dedupedUnlinked: UnlinkedRef[] = [];
  const seenUnlinked = new Map<string, UnlinkedRef>();

  for (const ref of unlinked) {
    const key = `${ref.paraIndex}|${ref.pattern}`;
    if (seenUnlinked.has(key)) continue;

    // Check for overlapping patterns in same paragraph
    // e.g., "Section 3.1" and "Section 3.1.2" — keep the longer match
    let superseded = false;
    for (const [existingKey, existingRef] of seenUnlinked) {
      if (existingRef.paraIndex !== ref.paraIndex) continue;
      // Same paragraph: check if one pattern contains the other
      if (existingRef.pattern.includes(ref.pattern)) {
        // Existing is longer and contains this one — skip this
        superseded = true;
        break;
      }
      if (ref.pattern.includes(existingRef.pattern)) {
        // This is longer — replace the existing
        seenUnlinked.delete(existingKey);
        break;
      }
    }

    if (!superseded) {
      seenUnlinked.set(key, ref);
    }
  }
  dedupedUnlinked.push(...seenUnlinked.values());

  console.log(`    Deduped unlinked: ${unlinked.length} → ${dedupedUnlinked.length}`);

  // ── Insert comments for unlinked references ──
  for (const ref of dedupedUnlinked) {
    if (commentsInserted >= MAX_COMMENTS) break;

    try {
      const results = body.search(ref.pattern, {
        matchCase: false,
        matchWholeWord: false,
        matchWildcards: false,
      });
      results.load("items");
      await context.sync();

      if (results.items.length === 0) continue;

      let commented = false;
      for (let i = 0; i < results.items.length && !commented; i++) {
        const range = results.items[i];
        const para = range.paragraphs.getFirst();
        para.load("style, text");
        await context.sync();

        const pStyle = para.style || "";
        const pText = (para.text || "").trim();

        // Skip TOC/caption/heading paragraphs
        if (isTocOrCaptionStyle(pStyle)) continue;
        if (isCaptionParagraph(pText)) continue;

        // Match to the correct paragraph using text prefix
        if (ref.paraText && pText.substring(0, 30) !== ref.paraText.substring(0, 30)) continue;

        // ── DEDUP CHECK: has this paragraph+pattern already been commented? ──
        const locationKey = `${pText.substring(0, 60)}|${ref.pattern}`;
        if (commentedLocations.has(locationKey)) {
          console.log(`    Skipped duplicate: "${ref.pattern}" in "${pText.substring(0, 40)}..."`);
          commented = true; // Mark as handled so we don't keep searching
          continue;
        }
        commentedLocations.add(locationKey);

        range.insertComment(
          `Cross-reference: "${ref.pattern}" appears as plain text. ` +
          `Per Section 6.1, use Insert → Cross-reference → ` +
          `"Only label and number" to create a proper hyperlink.`
        );

        // Navigation bookmark for Go To
        if (navCounter.count < MAX_NAV_ITEMS) {
          const bmName = BM_PREFIX + String(navCounter.count).padStart(3, "0");
          range.insertBookmark(bmName);
          navDetails.push(rangeDetail(bmName, ref.pattern, "unlinked \u2014 needs cross-reference"));
          navCounter.count++;
        }

        await context.sync();

        commentsInserted++;
        commented = true;
        console.log(`    Comment: "${ref.pattern}" in "${pText.substring(0, 50)}..."`);
      }
    } catch (e) {
      console.log(`    ⚠ Could not comment on "${ref.pattern}": ${(e as Error).message}`);
    }
  }

  // ── Insert comments for broken references ──
  for (const brk of broken) {
    if (commentsInserted >= MAX_COMMENTS) break;
    if (!brk.text || brk.text.length === 0) continue;

    // Dedup broken refs too
    const locationKey = `broken|${brk.text}|${brk.bookmark}`;
    if (commentedLocations.has(locationKey)) continue;
    commentedLocations.add(locationKey);

    try {
      const results = body.search(brk.text, {
        matchCase: true,
        matchWholeWord: false,
        matchWildcards: false,
      });
      results.load("items");
      await context.sync();

      if (results.items.length > 0) {
        // Only comment the first occurrence
        const para = results.items[0].paragraphs.getFirst();
        para.load("text");
        await context.sync();

        const pText = (para.text || "").trim();
        const rangeKey = `${pText.substring(0, 60)}|broken|${brk.text}`;
        if (commentedLocations.has(rangeKey)) continue;
        commentedLocations.add(rangeKey);

        results.items[0].insertComment(
          `Broken ${brk.source === "field" ? "cross-reference" : "hyperlink"}: ` +
          `"${brk.text}" targets bookmark "${brk.bookmark}" ` +
          `which does not exist. The target may have been deleted or renamed.`
        );

        // Navigation bookmark for Go To
        if (navCounter.count < MAX_NAV_ITEMS) {
          const bmName = BM_PREFIX + String(navCounter.count).padStart(3, "0");
          results.items[0].insertBookmark(bmName);
          navDetails.push(rangeDetail(bmName, brk.text, "broken \u2014 target missing"));
          navCounter.count++;
        }

        await context.sync();
        commentsInserted++;
        console.log(`    Comment: broken ${brk.source} "${brk.text}" → "${brk.bookmark}"`);
      }
    } catch (e) {
      console.log(`    ⚠ Could not comment on broken "${brk.text}": ${(e as Error).message}`);
    }
  }

  return commentsInserted;
}

// ─────────────────────────────────────────────────────────────────────────
// run24 — main entry point
// ─────────────────────────────────────────────────────────────────────────

export async function run24(): Promise<"Success" | ["Failure", string]> {
  try {
    // ── Read config ──
    const cfg = getConfig();
    const c   = cfg.run24_crossReferences as { patterns: { label: string; regex: string }[]; tocCaptionStyles: string[] };

    // Populate module-level TOC/caption style list from config
    _tocCaptionStyles = c.tocCaptionStyles;

    // Build regex patterns from config
    const xrefPatterns: { label: string; regex: RegExp }[] = c.patterns.map(p => ({
      label: p.label,
      regex: new RegExp(p.regex, "gi"),
    }));

    await Word.run(async (context) => {

      console.log("═".repeat(70));
      console.log("RUN 24: Cross-Reference Validation");
      console.log("═".repeat(70));

      // ── Step 0: Remove previous Run 24 comments ──
      // Prevents duplicates when the check is re-run on the same document.
      console.log("\n  Step 0: Clearing previous cross-reference comments...");
      try {
        const existingComments = context.document.body.getComments();
        existingComments.load("items");
        await context.sync();

        let removed = 0;
        for (let i = existingComments.items.length - 1; i >= 0; i--) {
          const comment = existingComments.items[i];
          comment.load("content");
          await context.sync();

          const content = comment.content || "";
          if (content.startsWith("Cross-reference:") || content.startsWith("Broken cross-reference:") || content.startsWith("Broken hyperlink:")) {
            comment.delete();
            removed++;
          }
        }
        if (removed > 0) {
          await context.sync();
          console.log(`    Removed ${removed} previous cross-reference comment(s)`);
        } else {
          console.log(`    No previous comments to clear`);
        }
      } catch (e) {
        console.log(`    ⚠ Could not clear previous comments: ${(e as Error).message}`);
      }

      // ── Step 1: Get body OOXML ──
      console.log("\n  Step 1: Loading document body OOXML...");
      const bodyOoxmlResult = context.document.body.getOoxml();
      await context.sync();

      const rawOoxml = bodyOoxmlResult.value;
      console.log(`  Body OOXML length: ${rawOoxml.length} chars`);

      // ── Step 2: Parse XML ──
      console.log("\n  Step 2: Parsing OOXML...");
      const xmlDoc = new DOMParser().parseFromString(rawOoxml, "application/xml");

      const parseError = xmlDoc.getElementsByTagName("parsererror");
      if (parseError.length > 0) {
        console.error("  ⚠ XML parse error:", parseError[0].textContent?.substring(0, 200));
        safeAlert("Cross-Reference Check Error\n\nCould not parse document XML.");
        return;
      }

      // ── Step 3: Extract field codes (the REAL cross-references) ──
      console.log("\n  Step 3: Extracting field codes (REF/PAGEREF/NOTEREF)...");
      const fieldRefs = extractFieldRefs(xmlDoc);

      const refFields     = fieldRefs.filter((f) => f.type === "REF");
      const pageRefFields = fieldRefs.filter((f) => f.type === "PAGEREF");
      const noteRefFields = fieldRefs.filter((f) => f.type === "NOTEREF");

      console.log(`    Total field cross-references : ${fieldRefs.length}`);
      console.log(`      REF fields                 : ${refFields.length}`);
      console.log(`      PAGEREF fields             : ${pageRefFields.length}`);
      console.log(`      NOTEREF fields             : ${noteRefFields.length}`);

      for (const f of fieldRefs.slice(0, 10)) {
        console.log(`      [${f.type}] "${f.resultText.substring(0, 40)}" → bm: "${f.bookmark}"`);
      }
      if (fieldRefs.length > 10) console.log(`      ... and ${fieldRefs.length - 10} more`);

      // ── Step 4: Extract hyperlinks ──
      console.log("\n  Step 4: Extracting w:hyperlink elements...");
      const hyperlinks = extractHyperlinks(xmlDoc);

      const tocLinks      = hyperlinks.filter((h) => h.type === "toc");
      const internalLinks = hyperlinks.filter((h) => h.type === "internal");
      const externalLinks = hyperlinks.filter((h) => h.type === "external");

      console.log(`    Total w:hyperlink elements   : ${hyperlinks.length}`);
      console.log(`      TOC entries                : ${tocLinks.length}`);
      console.log(`      Internal (non-TOC)         : ${internalLinks.length}`);
      console.log(`      External (r:id)            : ${externalLinks.length}`);

      // ── Step 5: Extract bookmarks ──
      console.log("\n  Step 5: Extracting bookmarks...");
      const bookmarks = extractBookmarks(xmlDoc);

      const tocBm   = bookmarks.filter((b) => b.name.startsWith("_Toc"));
      const refBm   = bookmarks.filter((b) => b.name.startsWith("_Ref"));
      const enrefBm = bookmarks.filter((b) => b.name.startsWith("_ENREF"));
      const otherBm = bookmarks.filter((b) =>
        !b.name.startsWith("_Toc") && !b.name.startsWith("_Ref") &&
        !b.name.startsWith("_Hlt") && !b.name.startsWith("_Hlk") &&
        !b.name.startsWith("_ENREF")
      );

      console.log(`    Total bookmarks: ${bookmarks.length}`);
      console.log(`      _Toc   : ${tocBm.length}`);
      console.log(`      _Ref   : ${refBm.length}`);
      console.log(`      _ENREF : ${enrefBm.length}`);
      console.log(`      Other  : ${otherBm.length}`);

      // ── Step 6: Extract paragraph data ──
      console.log("\n  Step 6: Extracting paragraph data...");
      const paraData = extractParaData(xmlDoc, fieldRefs, hyperlinks);
      const captionParas = paraData.filter((p) => p.isCaption);
      console.log(`    Total paragraphs : ${paraData.length}`);
      console.log(`    Caption paras    : ${captionParas.length} (excluded from validation)`);

      // ── Step 7: Find unlinked references ──
      console.log("\n  Step 7: Finding unlinked cross-references...");
      const unlinkedRefs = findUnlinkedReferences(paraData, xrefPatterns);

      console.log(`    Unlinked references found: ${unlinkedRefs.length}`);
      for (const ref of unlinkedRefs.slice(0, 15)) {
        console.log(`      "${ref.pattern}" [${ref.label}] in: "${ref.paraText.substring(0, 60)}..."`);
      }
      if (unlinkedRefs.length > 15) console.log(`      ... and ${unlinkedRefs.length - 15} more`);

      // ── Step 8: Find broken references ──
      console.log("\n  Step 8: Checking for broken cross-references...");
      const brokenRefs = findBrokenRefs(fieldRefs, hyperlinks, bookmarks);

      console.log(`    Broken references found: ${brokenRefs.length}`);
      for (const b of brokenRefs.slice(0, 10)) {
        console.log(`      [${b.source}] "${b.text.substring(0, 40)}" → missing "${b.bookmark}"`);
      }

      // ── Step 9: Categorize ──
      console.log("\n  Step 9: Categorizing references...");
      const counts = categorizeRefs(fieldRefs, unlinkedRefs);

      console.log(`    Table    — linked: ${counts.tableRefs.linked}, unlinked: ${counts.tableRefs.unlinked}`);
      console.log(`    Figure   — linked: ${counts.figureRefs.linked}, unlinked: ${counts.figureRefs.unlinked}`);
      console.log(`    Section  — linked: ${counts.sectionRefs.linked}, unlinked: ${counts.sectionRefs.unlinked}`);
      console.log(`    Appendix — linked: ${counts.appendixRefs.linked}, unlinked: ${counts.appendixRefs.unlinked}`);

      // ── Step 10: Insert comments + navigation bookmarks ──
      const hasIssues = unlinkedRefs.length > 0 || brokenRefs.length > 0;
      let commentsInserted = 0;
      const navDetails: RunDetail[] = [];
      const navCounter = { count: 0 };

      if (hasIssues) {
        console.log("\n  Step 10: Inserting comments + bookmarks at issues...");

        commentsInserted = await insertCommentsAtIssues(context, unlinkedRefs, brokenRefs, navDetails, navCounter);
        console.log(`    Comments inserted: ${commentsInserted}`);
        console.log(`    Go To bookmarks:   ${navCounter.count}`);
      } else {
        console.log("\n  Step 10: No issues found — skipping comment insertion.");
      }

      // ── Reset to editing mode ──
      context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
      await context.sync();

      // ── Summary ──
      const totalLinked   = counts.tableRefs.linked + counts.figureRefs.linked +
                            counts.sectionRefs.linked + counts.appendixRefs.linked;
      const totalUnlinked = counts.tableRefs.unlinked + counts.figureRefs.unlinked +
                            counts.sectionRefs.unlinked + counts.appendixRefs.unlinked;

      console.log("\n" + "═".repeat(70));
      console.log("CROSS-REFERENCE VALIDATION SUMMARY");
      console.log("═".repeat(70));
      console.log(`Field cross-refs (REF/PAGEREF)  : ${fieldRefs.length}`);
      console.log(`  REF       : ${refFields.length}`);
      console.log(`  PAGEREF   : ${pageRefFields.length}`);
      console.log(`  NOTEREF   : ${noteRefFields.length}`);
      console.log(`w:hyperlinks                    : ${hyperlinks.length}`);
      console.log(`  TOC       : ${tocLinks.length}`);
      console.log(`  Non-TOC   : ${internalLinks.length}`);
      console.log(`  External  : ${externalLinks.length}`);
      console.log(`Bookmarks                       : ${bookmarks.length}`);
      console.log(`${"─".repeat(40)}`);
      console.log(`Ref patterns found              : ${totalLinked + totalUnlinked}`);
      console.log(`  Linked (field code)            : ${totalLinked}`);
      console.log(`  Unlinked (plain text)          : ${totalUnlinked}`);
      console.log(`  Broken (target missing)        : ${brokenRefs.length}`);
      console.log(`${"─".repeat(40)}`);
      console.log(`Table    : ${counts.tableRefs.linked} linked, ${counts.tableRefs.unlinked} unlinked`);
      console.log(`Figure   : ${counts.figureRefs.linked} linked, ${counts.figureRefs.unlinked} unlinked`);
      console.log(`Section  : ${counts.sectionRefs.linked} linked, ${counts.sectionRefs.unlinked} unlinked`);
      console.log(`Appendix : ${counts.appendixRefs.linked} linked, ${counts.appendixRefs.unlinked} unlinked`);
      console.log(`${"─".repeat(40)}`);
      console.log(`Comments inserted               : ${commentsInserted}`);
      console.log("═".repeat(70));

      // ── Build structured result ──
      const resultDetails: RunDetail[] = [
        { label: 'Field cross-refs (REF/PAGEREF/NOTEREF)', count: fieldRefs.length },
        { label: 'Hyperlinks (TOC/internal/external)', count: hyperlinks.length },
        { label: 'Bookmarks', count: bookmarks.length },
        { label: 'Properly linked references', count: totalLinked },
        { label: 'Unlinked (plain text)', count: totalUnlinked },
        { label: 'Broken (target missing)', count: brokenRefs.length },
        { label: 'Comments inserted', count: commentsInserted },
        ...navDetails,
      ];

      if (hasIssues) {
        reportResult('Cross-Reference Validation \u2014 issues found', resultDetails);
      } else {
        reportResult('Cross-Reference Validation \u2014 all linked', resultDetails);
      }
    });

    return "Success";

  } catch (error) {
    console.error("run24 error:", error);
    safeAlert("Error in Cross-Reference check:\n" + (error as Error).message);
    return ["Failure", (error as Error).message];
  }
}